/**
 * Which signed-out screen the root path shows (App.tsx). Pure, so the order is testable.
 *
 * 1. A screen the visitor navigated to in this page (Sign up, Sign in, Profiles).
 * 2. `?view=` in the address.
 * 3. A share link's intent (`?intent=remix|make_own|avatar|save`, game-on-freeform U10):
 *    on a trusted device, the profile picker, so a kid with no password signs in with one tap
 *    and the intent (still in the address) takes them back; otherwise the sign-up form with
 *    its ribbon, never the landing page or the sign-in default. A kid sent to sign-up from a
 *    share page had only a password form they could not use (the operator, 2026-10-27). It
 *    waits on the trust check like the default. The address decides, not sessionStorage, so
 *    an intent left in the tab never takes over a later visit to the home page.
 * 4. The default: the profile picker on a trusted device, sign-in after a logout, otherwise
 *    the landing page. `null` while the device trust check is still out (only the default
 *    waits on it).
 */
import { toIntent } from "./share/useIntent";

export type UnauthView = "landing" | "welcome-back" | "signup" | "login";

export function unauthViewFor(opts: {
  navigated: UnauthView | undefined;
  search: string;
  serverTrusted: boolean | null;
  loggedOut: boolean;
}): UnauthView | null {
  const params = new URLSearchParams(opts.search);
  const queryView = params.get("view") as UnauthView | null;
  const explicit = opts.navigated ?? queryView;
  if (explicit) return explicit;
  const intent = toIntent(params.get("intent"), params.get("source"));
  if (opts.serverTrusted === null) return null;
  if (intent) return opts.serverTrusted ? "welcome-back" : "signup";
  return opts.serverTrusted ? "welcome-back" : opts.loggedOut ? "login" : "landing";
}
